
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
import SampleCode from '../../../../components/docs/plaidoms/SampleCode';

export const metadata = {
  toc: true,
  layout: 'guide',
  maxTocLevel: 4,
  description: 'Use Dwolla with Plaid Auth to send and receive payments'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Dwolla to your app' subtitle={metadata.description} mdxType="Header" />
    <Image src='/assets/img/docs/plaid-dwolla-partnership.png' alt="Plaid and Dwolla logos, side by side." mdxType="Image" />
    <br />
    <p>{`Plaid and Dwolla have partnered to offer businesses an easier way to connect to
the U.S. banking system. Plaid enables businesses to instantly authenticate a
customer's bank account, giving them the ability to leverage the Dwolla API to
connect to the ACH or RTP® networks for sending and receiving payments.
Dwolla's solution offers frictionless ACH or real-time payments for
companies looking to automate their current payments process and scale their business.`}</p>
    <p>{`With the Plaid + Dwolla integration, your users can verify their accounts in seconds by inputting their banking credentials in Plaid's front-end module. Plaid's mobile-friendly module handles input validation, error handling, and multi-factor authentication–providing a seamless onboarding experience to convert more users for your business.`}</p>
    <p>{`As part of the integration, Dwolla customers can access Plaid's full suite of APIs for clean, categorized transaction data, real-time balances, and more.`}</p>
    <h4 {...{
      "id": "getting-started"
    }}>{`Getting started`}</h4>
    <p>{`You'll first want to familiarize yourself with `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Plaid Link`}</a>{`, a drop-in client-side integration for the Plaid API that handles input validation, error handling, and multi-factor authentication.`}</p>
    <p>{`Your customers will use Link to authenticate with their financial institution and select the depository account they wish to use for ACH or RTP® transactions. From there, you'll receive a Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, allowing you to leverage real-time balance checks and transaction data, and a Dwolla `}<inlineCode parentName="p">{`processor_token`}</inlineCode>{`, which allows you to quickly and securely verify a bank funding source via `}<a parentName="p" {...{
        "href": "https://www.dwolla.com/?utm_campaign=Plaid-Documentation&utm_source=Plaid&utm_medium=Referral"
      }}>{`Dwolla's API`}</a>{` without having to store any sensitive banking information. Utilizing Plaid + Dwolla enables a seamless workflow for sending and receiving payments.`}</p>
    <p>{`As a complement to this guide, you can also use `}<a parentName="p" {...{
        "href": "https://developers.dwolla.com/docs/secure-exchange/plaid"
      }}>{`Dwolla's guide to integrating with Plaid`}</a>{`.`}</p>
    <h4 {...{
      "id": "instructions"
    }}>{`Instructions`}</h4>
    <h5 {...{
      "id": "set-up-your-plaid-and-dwolla-accounts"
    }}>{`Set up your Plaid and Dwolla accounts`}</h5>
    <p>{`You'll need accounts at both Plaid and Dwolla in order to use the Plaid + Dwolla integration. You'll also need to be a Dwolla customer in order to add a bank funding source.`}</p>
    <p>{`First, `}<a parentName="p" {...{
        "href": "https://accounts.dwolla.com/sign-up/pay-as-you-go?utm_campaign=Plaid-Documentation&utm_source=Plaid&utm_medium=Referral"
      }}>{`sign up for a Dwolla account`}</a>{` if you don't already have one.`}</p>
    <p>{`Next, verify that your Plaid account is enabled for the integration. If you do not have a Plaid account, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup/dwolla"
      }}>{`create one`}</a>{`. Your account will be automatically enabled for integration access.`}</p>
    <p>{`To verify that your Plaid account is enabled for the integration, go to the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/integrations"
      }}>{`Integrations`}</a>{` section of the account dashboard. If the integration is off, simply click the 'Enable' button for Dwolla to enable the integration.`}</p>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "https://developers.dwolla.com/docs/testing"
      }}>{`Dwolla Sandbox`}</a>{` to test the Plaid + Dwolla integration for free.`}</p>
    <h5 {...{
      "id": "complete-your-plaid-application-profile-and-company-profile"
    }}>{`Complete your Plaid application profile and company profile`}</h5>
    <p>{`After connecting your Plaid and Dwolla accounts, you'll need to complete your Plaid `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` in the Dashboard, which involves filling out basic information about your app, such as your company name and website. This step helps your end-users learn more about how your product uses their bank information and is also required for connecting to some banks.`}</p>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <p>{`In order to integrate with Plaid Link, you will first need to create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`. A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`
is a short-lived, one-time use token that is used to authenticate your app with Link. To create one,
make a `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request with your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and a few other
required parameters from your app server. View the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` documentation for a full list of `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` configurations.`}</p>
    <p>{`To see your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, visit the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`Plaid Dashboard`}</a>{`.`}</p>
    <SampleCode.CreateLinkToken product='auth' />
    <h5 {...{
      "id": "integrate-with-plaid-link"
    }}>{`Integrate with Plaid Link`}</h5>
    <p>{`Once you have a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, all it takes is a few lines of client-side JavaScript to launch Link.
Then, in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback, you can call a simple server-side handler to exchange the Link `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`
for a Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and a Dwolla `}<inlineCode parentName="p">{`processor_token`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "#Integrate Link",
        "#Integrate": true,
        "Link": true
      }}>{`<button id="linkButton">Open Link - Institution Select</button>
<script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
<script>
  let linkHandler;
  (async function(){
    linkHandler = Plaid.create({
      // Make a request to your server to fetch a new link_token.
      token: (await $.post('/create_link_token')).link_token,
      onLoad: function() {
          // The Link module finished loading.
      },
      onSuccess: function(public_token, metadata) {
        // The onSuccess function is called when the user has
        // successfully authenticated and selected an account to
        // use.
        //
        // When called, you will send the public_token and the selected accounts,
        // metadata.accounts, to your backend app server.

        sendDataToBackendServer({
           public_token: public_token,
           accounts: metadata.accounts
        });
        console.log('Public Token: ' + public_token);
        console.log('Customer-selected account ID: ' + metadata.accounts[0].id);
      },
      onExit: function(err, metadata) {
        // The user exited the Link flow.
        if (err != null) {
            // The user encountered a Plaid API error
            // prior to exiting.
        }
        // metadata contains information about the institution
        // that the user selected and the most recent
        // API request IDs.
        // Storing this information can be helpful for support.
      },
    });
  })();

  // Trigger the authentication view
  document.getElementById('linkButton').onclick = function() {
    linkHandler.open();
  };
</script>
`}</code></pre>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/docs/link/web#create"
      }}>{`Link parameter reference`}</a>{` for complete documentation on possible configurations.`}</p>
    <p><inlineCode parentName="p">{`Plaid.create`}</inlineCode>{` accepts one argument, a configuration `}<inlineCode parentName="p">{`Object`}</inlineCode>{`, and returns an `}<inlineCode parentName="p">{`Object`}</inlineCode>{` with four functions, `}<a parentName="p" {...{
        "href": "/docs/link/web#open"
      }}><inlineCode parentName="a">{`open`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/web#exit"
      }}><inlineCode parentName="a">{`exit`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/web#submit"
      }}><inlineCode parentName="a">{`submit`}</inlineCode></a>{`, and `}<a parentName="p" {...{
        "href": "/docs/link/web#destroy"
      }}><inlineCode parentName="a">{`destroy`}</inlineCode></a>{`. Calling `}<inlineCode parentName="p">{`open`}</inlineCode>{` will display the "Institution Select" view, calling `}<inlineCode parentName="p">{`exit`}</inlineCode>{` will close Link, and calling `}<inlineCode parentName="p">{`destroy`}</inlineCode>{` will clean up the iframe.`}</p>
    <h5 {...{
      "id": "write-server-side-handler"
    }}>{`Write server-side handler`}</h5>
    <p>{`The Link module handles the entire onboarding flow securely and quickly, but does not actually retrieve account data for a user. Instead, the Link module returns a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` and an `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array, which is a property on the `}<inlineCode parentName="p">{`metadata`}</inlineCode>{` object, via the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback. Exchange this `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for a Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` using the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` API endpoint.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array will contain information about bank accounts associated with the credentials entered by the user, and may contain multiple accounts if the user has more than one bank account at the institution. In order to avoid any confusion about which account your user wishes to use with Dwolla, it is recommended to set `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link/account-select"
      }}>{`Account Select`}</a>{` to "Single select" in the Plaid Dashboard. When this setting is selected, the `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array will always contain exactly one account.`}</p>
    <p>{`Once you have identified the account you will use, you will send the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` property of the account to Plaid, along with the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, to create a Dwolla `}<inlineCode parentName="p">{`processor_token`}</inlineCode>{`. You'll send this token to Dwolla and they will use it to securely retrieve account and routing numbers from Plaid.`}</p>
    <MDXCodeBlock type='server' title='Create a Dwolla processor token' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`# Exchange the public token from Plaid Link for an access token.
curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "public_token": "\${PUBLIC_TOKEN}"
  }' \\
  -X POST \\
  https://sandbox.plaid.com/item/public_token/exchange

# Create a processor token for a specific account id.
curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": "\${ACCESS_TOKEN}",
    "account_id": "\${ACCOUNT_ID}",
    "processor": "dwolla"
  }' \\
  -X POST \\
  https://sandbox.plaid.com/processor/token/create
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.ItemPublicTokenExchangeRequest;
import com.plaid.client.model.ItemPublicTokenExchangeResponse;
import com.plaid.client.model.ProcessorTokenCreateRequest;
import com.plaid.client.model.ProcessorTokenCreateResponse;

// Change sandbox to production when you're ready to go live!
HashMap<String, String> apiKeys = new HashMap<String, String>();
apiKeys.put("clientId", plaidClientId);
apiKeys.put("secret", plaidSecret);
apiKeys.put("plaidVersion", "2020-09-14");
apiClient = new ApiClient(apiKeys);
apiClient.setPlaidAdapter(ApiClient.Sandbox);

plaidClient = apiClient.createService(PlaidApi.class);

// Exchange the public token from Plaid Link for an access token.
ItemPublicTokenExchangeRequest request = new ItemPublicTokenExchangeRequest()
  .publicToken(publicToken);

Response<ItemPublicTokenExchangeResponse> exchangeResponse = plaidClient
  .itemPublicTokenExchange(request)
  .execute();

// Create a processor token for a specific account id.
if (exchangeResponse.isSuccessful()) {
  String accessToken = exchangeResponse.body().getAccessToken();
  ProcessorTokenCreateRequest processorRequest = new ProcessorTokenCreateRequest()
    .accessToken(accessToken)
    .processor(ProcessorTokenCreateRequest.ProcessorEnum.DWOLLA)
    .accountId("FooBarAccountId");

  Response<ProcessorTokenCreateResponse> dwollaResponse = plaidClient
    .processorTokenCreate(processorRequest)
    .execute();

    if (dwollaResponse.isSuccessful()) {
      String dwollaProcessorToken = dwollaResponse.body().getProcessorToken();
    }
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

# Change sandbox to production when you're ready to go live!
configuration = Plaid::Configuration.new
configuration.server_index = Plaid::Configuration::Environment[ENV['PLAID_ENV'] || 'sandbox']
configuration.api_key['PLAID-CLIENT-ID'] = ENV['PLAID_CLIENT_ID']
configuration.api_key['PLAID-SECRET'] = ENV['PLAID_SECRET']
configuration.api_key['Plaid-Version'] = '2020-09-14'

api_client = Plaid::ApiClient.new(
  configuration
)

client = Plaid::PlaidApi.new(api_client)

# Exchange the public token from Plaid Link for an access token.
item_public_token_exchange_request = Plaid::ItemPublicTokenExchangeRequest.new
item_public_token_exchange_request.public_token = public_token

exchange_token_response = client.item_public_token_exchange(item_public_token_exchange_request)

access_token = exchange_token_response.access_token

# Create a processor token for a specific account id.
processor_token_create_request = Plaid::ProcessorTokenCreateRequest.new
processor_token_create_request.access_token = access_token
processor_token_create_request.account_id = account_id
processor_token_create_request.processor = "dwolla"

create_response = client.processor_token_create(processor_token_create_request)
processor_token = create_response.processor_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import plaid
from plaid.api import plaid_api
from plaid.model.item_public_token_exchange_request import ItemPublicTokenExchangeRequest
from plaid.model.processor_token_create_request import ProcessorTokenCreateRequest

# Change sandbox to production when you're ready to go live!
configuration = plaid.Configuration(
    host=plaid.Environment.Sandbox,
    api_key={
        'clientId': PLAID_CLIENT_ID,
        'secret': PLAID_SECRET,
        'plaidVersion': '2020-09-14'
    }
)
api_client = plaid.ApiClient(configuration)
client = plaid_api.PlaidApi(api_client)

# Exchange the public token from Plaid Link for an access token.
exchange_request = ItemPublicTokenExchangeRequest(public_token=public_token)
exchange_token_response = client.item_public_token_exchange(exchange_request)
access_token = exchange_token_response['access_token']

# Create a processor token for a specific account id.
create_request = ProcessorTokenCreateRequest(
    access_token=access_token,
    account_id=account_id,
    processor='dwolla'
)
create_response = client.processor_token_create(create_request)
processor_token = create_response['processor_token']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const {
  Configuration,
  PlaidApi,
  PlaidEnvironments,
  ProcessorTokenCreateRequest,
} = require('plaid');
// Change sandbox to production when you're ready to go live!
const configuration = new Configuration({
  basePath: PlaidEnvironments[process.env.PLAID_ENV],
  baseOptions: {
    headers: {
      'PLAID-CLIENT-ID': process.env.PLAID_CLIENT_ID,
      'PLAID-SECRET': process.env.PLAID_SECRET,
      'Plaid-Version': '2020-09-14',
    },
  },
});

const plaidClient = new PlaidApi(configuration);

try {
  // Exchange the public_token from Plaid Link for an access token.
  const tokenResponse = await plaidClient.itemPublicTokenExchange({
    public_token: publicToken,
  });
  const accessToken = tokenResponse.data.access_token;

  // Create a processor token for a specific account id.
  const request: ProcessorTokenCreateRequest = {
    access_token: accessToken,
    account_id: accountID,
    processor: 'dwolla',
  };
  const processorTokenResponse = await plaidClient.processorTokenCreate(
    request,
  );
  const processorToken = processorTokenResponse.data.processor_token;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
  "context"
  "os"

  plaid "github.com/plaid/plaid-go/v48/plaid"
)

// create the client
configuration := plaid.NewConfiguration()
configuration.AddDefaultHeader("PLAID-CLIENT-ID", os.Getenv("CLIENT_ID"))
configuration.AddDefaultHeader("PLAID-SECRET", os.Getenv("SECRET"))
configuration.UseEnvironment(plaid.Sandbox)
client := plaid.NewAPIClient(configuration)
ctx := context.Background()

// exchange the public_token for an access_token
exchangePublicTokenResp, _, err := client.PlaidApi.ItemPublicTokenExchange(ctx).ItemPublicTokenExchangeRequest(
  *plaid.NewItemPublicTokenExchangeRequest("PUBLIC_TOKEN"),
).Execute()
accessToken := exchangePublicTokenResp.GetAccessToken()

// get the account id
accountsResp, _, err := client.PlaidApi.AccountsGet(ctx).AccountsGetRequest(plaid.AccountsGetRequest{
  AccessToken: accessToken,
}).Execute()
accountID := accountsResp.GetAccounts()[0].GetAccountId()

// create a dwolla token for the specific account id
request := plaid.NewProcessorTokenCreateRequest(accessToken, accountID, "dwolla")
processorTokenResp, _, err := client.PlaidApi.ProcessorTokenCreate(ctx).ProcessorTokenCreateRequest(
  *request,
).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"
    "os"

    plaid "github.com/plaid/plaid-go/v49"
    plaidclient "github.com/plaid/plaid-go/v49/client"
    "github.com/plaid/plaid-go/v49/option"
)

// create the client
client := plaidclient.NewClient(
    option.WithClientID(plaid.String(os.Getenv("CLIENT_ID"))),
    option.WithSecret(os.Getenv("SECRET")),
    option.WithBaseURL(plaid.Environments.Sandbox),
)

// exchange the public_token for an access_token
exchangePublicTokenResp, err := client.Item.ExchangePublicToken(ctx, &plaid.ItemPublicTokenExchangeRequest{
    PublicToken: "PUBLIC_TOKEN",
})
accessToken := exchangePublicTokenResp.AccessToken

// get the account id
accountsResp, err := client.Accounts.Get(ctx, &plaid.AccountsGetRequest{
    AccessToken: accessToken,
})
accountID := accountsResp.Accounts[0].AccountID

// create a dwolla token for the specific account id
request := &plaid.ProcessorTokenCreateRequest{
    AccessToken: accessToken,
    AccountID:   accountID,
    Processor:   "dwolla",
}
processorTokenResp, err := client.Processor.CreateToken(ctx, request)
`}</code></pre>
    </MDXCodeBlock>
    <p>{`For a valid request, the API will return a JSON response similar to:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample response",
        "#Sample": true,
        "response": true
      }}>{`{
  "processor_token": "processor-sandbox-0asd1-a92nc",
  "request_id": "[Unique request ID]"
}
`}</code></pre>
    <h5 {...{
      "id": "make-a-request-to-dwolla"
    }}>{`Make a request to Dwolla`}</h5>
    <p>{`Once you've obtained the `}<inlineCode parentName="p">{`processor_token`}</inlineCode>{`, you'll then pass it to Dwolla as the value of the `}<inlineCode parentName="p">{`plaidToken`}</inlineCode>{` request parameter, along with a funding source `}<inlineCode parentName="p">{`name`}</inlineCode>{`, to create a funding source for a Dwolla Customer:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "#Create a Dwolla funding source",
        "#Create": true,
        "a": true,
        "Dwolla": true,
        "funding": true,
        "source": true
      }}>{`POST https://api-sandbox.dwolla.com/customers/AB443D36-3757-44C1-A1B4-29727FB3111C/funding-sources
Content-Type: application/vnd.dwolla.v1.hal+json
Accept: application/vnd.dwolla.v1.hal+json
Authorization: Bearer pBA9fVDBEyYZCEsLf/wKehyh1RTpzjUj5KzIRfDi0wKTii7DqY

{
  "plaidToken": "processor-sandbox-161c86dd-d470-47e9-a741-d381c2b2cb6f",
  "name": "Jane Doe's Checking"
}

...

HTTP/1.1 201 Created
Location: https://api-sandbox.dwolla.com/funding-sources/375c6781-2a17-476c-84f7-db7d2f6ffb31
`}</code></pre>
    <p>{`Once you've received a successful response from the Dwolla API, you'll use the unique funding source URL to identify the Customer's bank when `}<a parentName="p" {...{
        "href": "https://developers.dwolla.com/docs/api-reference/transfers/initiate-a-transfer?utm_campaign=Plaid-Documentation&utm_source=Plaid&utm_medium=Referral"
      }}>{`initiating ACH`}</a>{` or `}<a parentName="p" {...{
        "href": "https://developers.dwolla.com/docs/real-time-payments?utm_campaign=Plaid-Documentation&utm_source=Landing-Page&utm_medium=Referral"
      }}>{`RTP transfers`}</a>{`.`}</p>
    <h5 {...{
      "id": "testing-your-dwolla-integration"
    }}>{`Testing your Dwolla integration`}</h5>
    <p>{`You can create Dwolla `}<inlineCode parentName="p">{`processor_tokens`}</inlineCode>{` in Sandbox (sandbox.plaid.com, allows testing with simulated users) or Production (production.plaid.com, requires Dwolla Production credentials). `}</p>
    <p>{`To test the integration in Sandbox mode, simply use the Plaid `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials"
      }}>{`Sandbox credentials`}</a>{` when launching Link with a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` created in the Sandbox environment.`}</p>
    <Callout mdxType="Callout">
      <p>{`When testing in the Sandbox, you have the option to use the `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` endpoint instead of the end-to-end Link flow to create a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`. When using the `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{`-based flow, the Account Select flow will be bypassed and the `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array will not be populated. On Sandbox, instead of using the `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array, you can call `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{` and test with any returned account ID associated with an account with the subtype `}<inlineCode parentName="p">{`checking`}</inlineCode>{` or `}<inlineCode parentName="p">{`savings`}</inlineCode>{`.`}</p>
    </Callout>
    <h5 {...{
      "id": "get-ready-for-production"
    }}>{`Get ready for production`}</h5>
    <p>{`Your account is immediately enabled for our Sandbox environment (`}<a parentName="p" {...{
        "href": "https://sandbox.plaid.com"
      }}>{`https://sandbox.plaid.com`}</a>{`). To move to Production, please request access from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`Dashboard`}</a>{`.`}</p>
    <h4 {...{
      "id": "example-code-in-plaid-pattern"
    }}>{`Example code in Plaid Pattern`}</h4>
    <p>{`For a real-life example of an app that incorporates the creation of processor tokens, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding"
      }}>{`Plaid Pattern Account Funding`}</a>{` sample app. Pattern Account Funding is a sample account funding app that creates a processor token to send to your payment partner. The processor token creation code can be found in `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding/blob/master/server/routes/items.js#L188-L195"
      }}>{`items.js`}</a>{`.`}</p>
    <h4 {...{
      "id": "support-and-questions"
    }}>{`Support and questions`}</h4>
    <p>{`Find answers to many common integration questions and concerns—such as pricing, sandbox and test mode usage, and more, in our `}<a parentName="p" {...{
        "href": "/docs"
      }}>{`docs`}</a>{`.`}</p>
    <p>{`If you're still stuck, open a `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new"
      }}>{`support ticket`}</a>{` with information describing the issue that you're experiencing and we'll get back to you as soon as we can.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "getting-started",
        level: 4,
        title: "Getting started",
      },
{
        id: "instructions",
        level: 4,
        title: "Instructions",
      },
{
        id: "example-code-in-plaid-pattern",
        level: 4,
        title: "Example code in Plaid Pattern",
      },
{
        id: "support-and-questions",
        level: 4,
        title: "Support and questions",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
